import {
  Component, OnInit, OnChanges, AfterViewInit, OnDestroy,
  ChangeDetectorRef,
  HostBinding, Input, Output, SimpleChanges,
  ViewChild, ElementRef, EventEmitter, NgZone,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import * as _ from 'lodash';
import * as elementResizeDetectorMaker from 'element-resize-detector';

import { Observable ,  Subscription ,  BehaviorSubject } from 'rxjs';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

import { SearchModel } from '../search.model';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { ActiveFiltersComponent } from '../active-filters/active-filters.component';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'search-input',
  templateUrl: 'search-input.component.html',
  styleUrls: ['search-input.component.scss'],
})
export class SearchInputComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  searchTerm = new UntypedFormControl();
  typeAheadControl: UntypedFormControl;
  typeaheadLoading: boolean;
  typeaheadNoResults: boolean;

  // filters
  filtersShowMoreWidth: number = 100; // width of `+N More` button
  filtersWrapWidth: number;
  filtersWidth: number;
  filtersHiddenCount: number = 0;
  placeholderWidth: number;
  erd: any;

  // vars for show input handling
  showInputSubj: BehaviorSubject<boolean>;
  showInput: boolean = false;

  @Input() placeholder: string = 'Search for a product by name or other information…';
  @Input() hasAdvancedSearch: boolean = false;

  /*
    searchByCall = false - hide Search button and run search
    on keydown inside input

    searchByCall = true - show Search button and run search
    on Search button click or on Enter keydown
  */
  @Input() searchByCall: boolean = false;

  @Input() ghostValue: string;
  @Input() advancedSearchOpened: boolean = false;
  @Input() searchModel: SearchModel;
  @Input() filters: Array<ActiveFilterModel> = [];

  // typeahead inputs
  @Input() isTypeahead: boolean = false;
  @Input() dataSource: Observable<any>;
  @Input() typeaheadOptionField: string = 'option';

  // small search icon and placeholder styles
  @HostBinding('class.size-small')
  @Input() isSmall: boolean = false;

  @ViewChild('searchInputWrap', {static: false}) inputWrap: ElementRef | void;
  @ViewChild('searchInput', {static: false}) input: ElementRef | void;
  @ViewChild('searchInputPlaceholder', {static: false}) inputPlaceholder: ElementRef | void;
  @ViewChild('searchInputPlaceholderWrap', {static: false}) inputPlaceholderWrap: ElementRef | void;
  @ViewChild(ActiveFiltersComponent, {static: false}) activeFiltersComponent: ActiveFiltersComponent | void;

  @Output() clearAll: EventEmitter<string> = new EventEmitter();
  @Output() listIconClick: EventEmitter<boolean> = new EventEmitter();

  @Output() advancedSearchToggle: EventEmitter<boolean> = new EventEmitter();

  @Output() searchCall: EventEmitter<boolean> = new EventEmitter();   // Search button or Enter was clicked
  @Output() cancelClick: EventEmitter<boolean> = new EventEmitter();
  @Output() typeaheadSelect: EventEmitter<any> = new EventEmitter();
  @Output() filterRemove: EventEmitter<any> = new EventEmitter();
  @Output() clearSearchClick: EventEmitter<any> = new EventEmitter();

  private isActive: boolean = true;
  private isViewInited: boolean = false;

  constructor(
    private ngZone: NgZone,
    private ref: ChangeDetectorRef,
    private el: ElementRef,
  ) {
    this.showInputSubj = <BehaviorSubject<boolean>>new BehaviorSubject(false);
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get hasListFilter(): boolean {
    return _.some(this.filters, {type: 'list'});
  }

  get searchTermValue(): string {
    return this.searchTerm.value || '';
  }

  get typeAheadValue(): string {
    return this.typeAheadControl ? this.typeAheadControl.value : '';
  }

  get hasFilters(): boolean {
    if (this.filters) {
      return _.get(this.filters, 'length') > 0;
    }
    return false;
  }

  get hasString(): boolean {
    return !!_.get(this.searchTerm, 'value');
  }

  get isTextSearch(): boolean {
    return this.showInput && !this.advancedSearchOpened;
  }

  get hasSearchData(): boolean {
    return (this.hasFilters || this.hasString);
  }

  get showClear(): boolean {
    if (!this.hasAdvancedSearch) { return !this.showInput && this.hasSearchData; }
    return !this.showInput && !this.advancedSearchOpened && this.hasSearchData;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    this.showInputSubj
      .pipe(takeWhile(() => this.isActive))
      .subscribe((show: boolean) => {
        this.showInput = show;
        if (this.showInput) {
          _.delay(() => {
            if (this.input) {
              this.input.nativeElement.focus();
            }
          }, 100);
        }
        this.ref.markForCheck();
      });
    if (this.isTypeahead) {
      this.typeAheadControl = new UntypedFormControl();
    } else {
      this.searchTerm.setValue(this.searchModel.searchValue);
    }
  }

  ngAfterViewInit() {
    _.delay(() => this.updateSizeData(), 100);

    this.erd = elementResizeDetectorMaker({
      strategy: 'scroll', // <- For ultra performance.
    });

    if (this.inputWrap) {
      this.erd.listenTo(this.inputWrap.nativeElement, (element) => {
        _.delay(() => this.updateSizeData(), 100);
      });
    }

    this.isViewInited = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.filters && this.isViewInited) {
      _.delay(() => this.updateSizeData(), 100);
    }
  }

  ngOnDestroy() {
    if (this.erd && this.input) { this.erd.removeListener(this.input.nativeElement); }
    this.isActive = false;
    this.isViewInited = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public resetValue(value: any = '') {
    _.defer(() => this.searchTerm.reset(value));
  }

  // typeahead handlers
  public handleTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  public handleTypeaheadNoResults(e: boolean): void {
    this.typeaheadNoResults = e;
  }

  public handleTypeaheadOnBlur(e: any) {
    this.typeAheadControl.setValue(null);
  }

  public toggleSearchInput(show: boolean) {
    _.delay(() => this.showInputSubj.next(show), 100);
  }

  /*******************************************
   * COMPONENT EVENT HANDLERS
   *******************************************/

  onSearchClick() {
    this.callSearch();
  }

  onEnter() {
    this.callSearch();
  }

  onCancelBtnClick() {
    this.toggleSearchInput(false);
    this.resetValue(this.searchModel.searchValue);
    this.cancelClick.emit();
  }

  onAdvancedSearchClick() {
    this.advancedSearchToggle.emit();
  }

  onSelectTypeahead(e: TypeaheadMatch): void {
    this.typeAheadControl.setValue(null);
    this.typeaheadSelect.emit(e);
    this.toggleSearchInput(false);
  }

  onSearchInputBlur() {
    this.toggleSearchInput(false);
  }

  onFilterRemove($event: any) {
    this.filterRemove.emit($event);
    _.delay(() => this.updateSizeData(), 100);
  }

  onClearSearchClick() {
    if (!this.isTypeahead) {
      this.searchTerm.setValue('');
    }
    this.clearSearchClick.emit();
    _.delay(() => this.updateSizeData(), 100);
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private updateSizeData() {
    if (!this.filters || !this.filters.length) {
      this.filtersWidth = 0;
      this.filtersHiddenCount = 0;
      this.placeholderWidth = null;
      this.filtersWrapWidth = 0;
      return;
    }

    const currPlaceholderWidth = this.inputPlaceholder && this.getActualElWidth(this.inputPlaceholder.nativeElement);
    const placeholderWrapWidth = this.inputPlaceholderWrap && this.inputPlaceholderWrap.nativeElement.offsetWidth;

    const hasSearchValue = (!this.isTypeahead && this.searchTerm.value);
    const delta = 5; // space for filters
    // LEAVE IT FOR TESTING
    // console.log(`currPlaceholderWidth = ${currPlaceholderWidth}`);
    // console.log(`placeholderWrapWidth = ${placeholderWrapWidth}`);

    // calculate actual filters list width
    const filtersDataList = _.get(this.activeFiltersComponent, 'activeFilterItemsDataList');

    // LEAVE IT FOR TESTING
    // console.log(filtersDataList);

    let filtersActualWidth;
    if (filtersDataList) {
      filtersActualWidth = _.sum(_.map(filtersDataList, 'width')) + delta; // add some space
    }
    // LEAVE IT FOR TESTING
    // console.log(`filtersActualWidth = ${filtersActualWidth}`);

    // check if placeholder needs to decrease
    const isDecreasePlaceholder = (filtersActualWidth && currPlaceholderWidth &&
      (currPlaceholderWidth + filtersActualWidth > placeholderWrapWidth));

    // LEAVE IT FOR TESTING
    // console.log(`isDecreasePlaceholder = ${isDecreasePlaceholder}`);

    // calculate all available space for filters
    const minPlaceholderWidth = hasSearchValue ? Math.floor(placeholderWrapWidth / 3) : 0; // at least 1/3 of the space
    const filtersAvailableWidth = hasSearchValue ?
      (placeholderWrapWidth - (isDecreasePlaceholder ?  minPlaceholderWidth : currPlaceholderWidth) - this.filtersShowMoreWidth) :
      placeholderWrapWidth;
    // LEAVE IT FOR TESTING
    // console.log(`filtersAvailableWidth = ${filtersAvailableWidth}`);
    // console.log(`minPlaceholderWidth = ${minPlaceholderWidth}`);

    if (filtersAvailableWidth && filtersDataList) {

      const filtersMaxWidth = filtersAvailableWidth;
      // LEAVE IT FOR TESTING
      // console.log(`filtersMaxWidth = ${filtersMaxWidth}`);

      let actualWidth = 0;
      let hiddenCount = this.filters.length;
      _.forEach(filtersDataList, (filter) => {
        actualWidth += filter.width;
        // LEAVE IT FOR TESTING
        // console.log(`actualWidth = ${actualWidth}`);
        if (actualWidth < filtersMaxWidth || filter.type === 'list') {
          this.filtersWidth = actualWidth;
          hiddenCount--;
        }
      });
      if (hasSearchValue) {
        this.filtersWrapWidth = this.filtersWidth + (hiddenCount ? this.filtersShowMoreWidth : 0);
        this.placeholderWidth = placeholderWrapWidth - this.filtersWrapWidth;
      } else {
        this.filtersWrapWidth = placeholderWrapWidth - (hiddenCount ? this.filtersShowMoreWidth : 0);
        this.filtersWidth = this.filtersWrapWidth; // expand it
        this.placeholderWidth = 0;
      }
      this.filtersHiddenCount = hiddenCount;
    } else {
      this.filtersWidth = this.filtersWrapWidth;
      this.filtersHiddenCount = 0;
      this.placeholderWidth = null;
      this.filtersWrapWidth = 0;
    }
    // LEAVE IT FOR TESTING
    // console.log(`filtersWidth = ${this.filtersWidth}`);
    // console.log(`filtersHiddenCount = ${this.filtersHiddenCount}`);
    // console.log(`placeholderWidth = ${this.placeholderWidth}`);
    // console.log(`filtersWrapWidth = ${this.filtersWrapWidth}`);
    // console.log(`placeholderWidth = ${this.placeholderWidth}`);
    // console.log('-------------------------------------------');
    this.ref.markForCheck();
  }

  private getActualElWidth(element: any) {
    // create fake element, measure actual width, remove it
    const dupPlaceholder = element.cloneNode(true);
    dupPlaceholder.style.position = 'absolute';
    dupPlaceholder.style.width = 'auto';
    dupPlaceholder.style.visibility = 'hidden';
    this.el.nativeElement.appendChild(dupPlaceholder);
    _.delay(() => this.el.nativeElement.removeChild(dupPlaceholder), 50);
    return dupPlaceholder.offsetWidth;
  }

  private callSearch() {
    this.searchCall.emit();
    if (this.input) {
      this.input.nativeElement.blur();
    }
  }
}
